// src/pages/NetworkAlerts/GroupPicker.js — seleção em cascata (Agrupar por → Grupo) para ERPs indexados
import React, { useEffect, useMemo, useState } from "react";

import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import CircularProgress from "@material-ui/core/CircularProgress";
import Autocomplete from "@material-ui/lab/Autocomplete";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";

import useStyles from "./styles";
import { toNumber } from "./constants";

// Agrupamentos oferecidos no índice local (ordem do select).
export const GROUP_BY_TYPES = ["olt", "pon", "city", "region"];

export const groupByLabel = (type) => i18n.t(`networkAlerts.groups.by.${type}`);

const NO_PARENT = "__none__";

const normalizeGroups = (data) => {
  const list = Array.isArray(data?.groups) ? data.groups : [];
  return list
    .filter((g) => g && g.value !== undefined && g.value !== null && String(g.value) !== "")
    .map((g) => ({
      value: String(g.value),
      count: toNumber(g.count),
      parent: g.parent ? String(g.parent) : null,
    }));
};

const collator = new Intl.Collator("pt-BR", {
  numeric: true,
  sensitivity: "base",
});

// Autocomplete exige as opções ordenadas pelo grupo para não repetir cabeçalhos.
const sortGroups = (groups, by) =>
  [...groups].sort((a, b) => {
    if (by === "pon") {
      const pa = a.parent || "";
      const pb = b.parent || "";
      if (pa !== pb) return pa === "" ? 1 : pb === "" ? -1 : collator.compare(pa, pb);
    }
    return collator.compare(a.value, b.value);
  });

/** Texto exibido no campo e usado pelo filtro de digitação. */
export const groupOptionLabel = (option, by) => {
  if (!option) return "";
  if (by === "region" && option.parent) return `${option.value} (${option.parent})`;
  return option.value;
};

/**
 * Props:
 *  - erpIntegrationId (string|number), by (um de GROUP_BY_TYPES), onByChange(by)
 *  - selected (grupo ou null), onSelect(grupo|null)
 *  - reloadKey: mude para recarregar (ex.: após reindexação)
 *  - disabled: bloqueia os dois selects (índice vazio/indexando)
 *  - onLoaded({ total, rows, indexedAt }): meta da resposta de /groups
 */
const GroupPicker = ({
  erpIntegrationId,
  by,
  onByChange,
  selected,
  onSelect,
  reloadKey = 0,
  disabled = false,
  onLoaded,
}) => {
  const classes = useStyles();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!erpIntegrationId || !by || disabled) {
      setGroups([]);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setGroups([]);
    api
      .get("/network-alerts/groups", {
        params: { erpIntegrationId: Number(erpIntegrationId), by },
      })
      .then(({ data }) => {
        if (cancelled) return;
        setGroups(sortGroups(normalizeGroups(data), by));
        if (onLoaded) {
          onLoaded({
            total: toNumber(data?.total),
            rows: toNumber(data?.rows),
            indexedAt: data?.indexedAt || null,
          });
        }
      })
      .catch((err) => {
        if (!cancelled) toastError(err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // onLoaded é callback estável do pai; não deve disparar recarga.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erpIntegrationId, by, reloadKey, disabled]);

  // Mantém o objeto selecionado apontando para a lista atual (mesmo valor).
  const selectedOption = useMemo(() => {
    if (!selected) return null;
    return groups.find((g) => g.value === selected.value) || selected;
  }, [groups, selected]);

  const groupBy = (option) => {
    if (by !== "pon") return undefined;
    return option.parent || NO_PARENT;
  };

  const renderGroupHeader = (label) =>
    label === NO_PARENT ? i18n.t("networkAlerts.groups.noParent") : label;

  return (
    <>
      <TextField
        select
        className={classes.filterItem}
        value={by}
        onChange={(e) => onByChange(e.target.value)}
        variant="outlined"
        size="small"
        disabled={disabled}
        SelectProps={{
          renderValue: (v) => (
            <>
              <span className={classes.filterPrefix}>
                {i18n.t("networkAlerts.groups.by.label")}:
              </span>{" "}
              {groupByLabel(v)}
            </>
          ),
        }}
        inputProps={{ "aria-label": i18n.t("networkAlerts.groups.by.label") }}
      >
        {GROUP_BY_TYPES.map((type) => (
          <MenuItem key={type} value={type}>
            {groupByLabel(type)}
          </MenuItem>
        ))}
      </TextField>

      <Autocomplete
        className={classes.filterItemWide}
        classes={{
          paper: classes.groupPaper,
          option: classes.groupOption,
          groupLabel: classes.groupHeader,
        }}
        size="small"
        options={groups}
        value={selectedOption}
        loading={loading}
        disabled={disabled || !erpIntegrationId}
        onChange={(e, option) => onSelect(option || null)}
        getOptionLabel={(option) => groupOptionLabel(option, by)}
        getOptionSelected={(option, val) => option.value === val?.value}
        groupBy={by === "pon" ? groupBy : undefined}
        renderGroup={
          by === "pon"
            ? (params) => (
                <li key={params.key}>
                  <div className={classes.groupHeader}>{renderGroupHeader(params.group)}</div>
                  <ul className={classes.groupList}>{params.children}</ul>
                </li>
              )
            : undefined
        }
        renderOption={(option) => (
          <div className={classes.groupOptionRow}>
            <span className={classes.groupOptionLabel}>
              {option.value}
              {by === "region" && option.parent && (
                <span className={classes.groupOptionParent}> ({option.parent})</span>
              )}
            </span>
            <span className={classes.groupOptionCount}>
              {i18n.t("networkAlerts.groups.clients", { count: option.count })}
            </span>
          </div>
        )}
        noOptionsText={i18n.t("networkAlerts.groups.noOptions")}
        loadingText={i18n.t("networkAlerts.groups.loading")}
        openOnFocus
        autoHighlight
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            placeholder={
              loading
                ? i18n.t("networkAlerts.groups.loading")
                : i18n.t("networkAlerts.groups.placeholder", {
                    total: groups.length,
                  })
            }
            inputProps={{
              ...params.inputProps,
              "aria-label": i18n.t("networkAlerts.groups.label"),
            }}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? <CircularProgress size={16} className={classes.groupSpinner} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
    </>
  );
};

export default GroupPicker;
